import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import TextField, { InputProps } from "features/components/TextField";

interface Props extends InputProps {
  fieldName: string;
  label?: string;
  value?: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
}

const getPadding = (type: string) => {
  if (type === "number") {
    return 20;
  }

  return 35;
};

const InputFilter = ({
  fieldName,
  label,
  value,
  type = "text",
  onChange,
  fullWidth,
  ...otherProps
}: Props) => {
  const { t } = useTranslation("filters");
  const placeholder = t("placeholders.enter_field", { field: type });
  const [calculatedWidth, setCalculatedWidth] = useState<number | undefined>(
    undefined
  );
  const textMeasureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (fullWidth) return;

    if (textMeasureRef.current && value !== undefined) {
      // Set the text content to measure
      textMeasureRef.current.textContent = value.toString();

      // Add padding to the width (adjust the values as needed)
      const measuredWidth =
        textMeasureRef.current.offsetWidth + getPadding(type);
      setCalculatedWidth(Math.max(measuredWidth, 120)); // Minimum width of 120px
    } else if (textMeasureRef.current) {
      textMeasureRef.current.textContent = placeholder
        ? placeholder.toString()
        : "";
      const measuredWidth = textMeasureRef.current.offsetWidth + 35;
      setCalculatedWidth(measuredWidth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <>
      <span
        ref={textMeasureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "inherit",
          fontSize: "inherit",
        }}
      />
      <TextField
        type={type}
        label={label}
        value={value}
        sx={
          !fullWidth
            ? { width: calculatedWidth, minWidth: "5rem", maxWidth: "20rem" }
            : {}
        }
        fullWidth={fullWidth}
        testId={`input-filter-${fieldName}`}
        onChange={onChange}
        placeholder={placeholder}
        margin="dense"
        {...otherProps}
      />
    </>
  );
};

export default InputFilter;
